import React, { useState, useEffect, useReducer, useContext } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@mui/styles';

import Popper from '@mui/material/Popper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import InputLabel from '@mui/material/InputLabel';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';

import CloseIcon from '@mui/icons-material/Close';

import Draggable from 'react-draggable';
import Moment from 'moment';
// import { DatePicker } from '@mui/x-date-pickers';
import { get as _get } from 'lodash';

import Validator, { useValidate, checkValidity } from '../../components/validator';
import useWindowSize from '../../libraries/usewindowsize';
import DatePicker from '../../components/styled/datetimepicker';
import StyledPaper from '../../components/styled/paper';
import DateRange from './widgets/components/date-range';
import DashboardService from '../../data/dashboard-service';
import { teal } from '../../data/colors';
import { User, ProfileContext } from '../../data/auth-service';

const useStyles = makeStyles(theme => ({
    root: {
        zIndex: theme.zIndex.modal,
        whiteSpace: 'nowrap',
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
        },
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
    },
    title: {
        cursor: 'move',
    },
    subtitle1: {
        ...theme.typography.subtitle1,
        paddingBottom: 5,
        color: theme.palette.primary.main,
    },
    paper: {
        maxWidth: 700,
        width: 700,
        '& .MuiListItemIcon-root': {
            minWidth: 0,
        },
        height: 'auto',
        // overflowY: 'auto',
    },
    inputEnd: {
        ...theme.typography.body1,
        verticalAlign: 'bottom',
        lineHeight: 3,
        opacity: 0.7,
        paddingRight: 5,
    },
    wrapText: {
        ...theme.typography.body1,
        verticalAlign: 'bottom',
        opacity: 0.7,
        paddingRight: 5,
        display: 'inline-block',
        whiteSpace: 'break-spaces'
    },
    addEmailBtn: {
        marginTop: 25,
    },
    next: {
        color: teal[300],
    },
    emailBody: {
        resize: 'vertical',
        backgroundColor: theme.styled.paper.background,
        color: theme.palette.primary.contrastText,
    },
    bodyLabel: {
        marginLeft: 10,
        marginBottom: 5,
        marginTop: 10,
        fontSize: 'small',
    },
    compactTypeWidthFix: {
        minWidth: '104px',
    }
}));

const PRIVAT = 'Private';
const PUBLICR = 'Public Read';
const PUBLICW = 'Public Write';
const SCOPES = [PRIVAT, PUBLICR, PUBLICW];
const COMPACTS = ['vertical', 'horizontal', 'none'];
const PERIODS = [
    'None',
    'Hourly',
    'Daily',
    'Weekday',
    'Weekly',
    'Monthly',
    'Yearly',
    'Quarterly',
    'Quarter',
];
const WEEKDAYS = [
    { key: 0, value: 'Sunday' },
    { key: 1, value: 'Monday' },
    { key: 2, value: 'Tuesday' },
    { key: 3, value: 'Wednesday' },
    { key: 4, value: 'Thursday' },
    { key: 5, value: 'Friday' },
    { key: 6, value: 'Saturday' },
];
const MONTHS = Moment.months().map((m, i) => ({ key: i + 1, value: m }));
const QUARTERS = [
    { key: 1, value: 'Q1' },
    { key: 2, value: 'Q2' },
    { key: 3, value: 'Q3' },
    { key: 4, value: 'Q4' },
];
const FIRST = <span>1<span style={{ fontSize: 'small' }}>st</span></span>;
const DEFAULT_SCHDULE = {
    interval: 42,
    period: 'None',
    weekday: 1,
    pause: false,
    date_override: false,
};
const DEFAULT_REPORT = {
    emails: [],
};
const DEFAULT_LAYOUT = {
    compact_type: 'vertical',
};

function PaperComponent (props) {
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const { updateProfile, getProfile } = useContext(ProfileContext);

    const handleOnDrag = (e, d) => setPosition(d);
    const handleOnStop = (e, d) => {
        setPosition(d);
        // stash position in user preference
        const profile = getProfile('dashboard') || {};
        updateProfile('dashboard', { ...getProfile('dashboard'), config: { ...(profile.config || {}), position: { x: d.x, y: d.y } } });
    };

    // initial hook
    useEffect(() => {
        const profile = getProfile('dashboard') || {};
        setPosition(_get(profile, 'config.position', { x: 0, y: 0 }));
    }, [getProfile]);

    // defense against the dark arts of moving the dialog out of bounds
    useEffect(() => {
        if (!props.kick) return;
        setPosition(current => ({ x: current.x, y: current.y < 0 ? 0 : current.y }));
    }, [props.kick]);

    return (
        <Draggable
            axis="none"
            position={{ x: position.x, y: position.y }}
            onDrag={handleOnDrag}
            onStop={handleOnStop}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}>
            <StyledPaper {...props}/>
        </Draggable>
    );
}
PaperComponent.propTypes = {
    kick: PropTypes.number,
};

const DashboardConfig = props => {
    const classes = useStyles();
    const theme = useTheme();
    const daForm = useValidate({});
    const emailForm = useValidate({});
    const { height } = useWindowSize();

    const [scope, setScope] = useState(PRIVAT);
    const [report, setReport] = useState(DEFAULT_REPORT);
    const [schedule, setSchedule] = useState(DEFAULT_SCHDULE);
    const [layout, setLayout] = useState(DEFAULT_LAYOUT);
    const [title, setTitle] = useState('');
    const [email, setEmail] = useState('');
    const [nextRun, setNextRun] = useState();
    const [range, setRange] = useState({});
    const [oldTitle, setOldTitle] = useState('');
    const [reposition, setReposition] = useState(0);

    // TODO:  I should be using fancy reducer in data pages
    const [names, setNames] = useReducer(() => props.names
        .filter(n => n !== props.dashboard.title), []);

    function handleListKeyDown (event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            props.onCancel();
        }
    }

    const handleOnOK = () => {
        // don't pass back pointers
        props.onOK({ ...props.dashboard, scope, layout, report, schedule, title }, oldTitle);
    };

    const handleEmailChange = (e, validID, valid) => {
        emailForm.set({ ...emailForm.get(), [validID]: valid });
        setEmail(e.target.value);
    };

    const handleTitleChange = (e, validID, valid) => {
        daForm.set({ ...daForm.get(), [validID]: valid });
        setTitle(e.target.value);
    };

    const handleIntervalChange = (e, validID, valid) => {
        daForm.set({ ...daForm.get(), [validID]: valid });
        setSchedule({ ...schedule, interval: +e.target.value });
    };

    const handleScopeChange = e => setScope(e.target.value);
    const handleCompactTypeChange = e => setLayout({ ...layout, compact_type: e.target.value });
    const handlePeriodChange = e => {
        setSchedule({ ...schedule, period: e.target.value });
    };
    const handleUnitsChange = e => setSchedule({ ...schedule, units: +e.target.value });
    const handleTimeChange = t => setSchedule({ ...schedule, time: t.format('HH:mm') });
    const handleWeekdayChange = e => setSchedule({ ...schedule, weekday: +e.target.value });
    const handleMonthChange = e => setSchedule({ ...schedule, month: +e.target.value });
    const handleQuarterChange = e => setSchedule({ ...schedule, quarter: +e.target.value });
    const handlePauseChanged = e => setSchedule({ ...schedule, pause: !e.target.checked });
    const handlePause = e => setSchedule({ ...schedule, pause: true });
    const handleUnpause = e => setSchedule({ ...schedule, pause: false });

    const handleHandleToggleOverride = e => setSchedule({
        ...schedule, date_override: !e.target.checked
    });
    const handleOverride = e => setSchedule({ ...schedule, date_override: true });
    const handleOverrideOff = e => setSchedule({ ...schedule, date_override: false });

    const handleEmailSubjectChange = e => setReport({ ...report, subject: e.target.value });
    const handleEmailBodyChange = e => setReport({ ...report, body: e.target.value });
    const handleAddEmailRecipient = () => {
        if (!email) return;

        if (!(report.emails || []).find(e => e === email)) {
            report.emails = report.emails || [];
            report.emails.push(email);
            setReport({ ...report });
            setEmail('');
        }
    };
    const handleRemoveEmailRecipient = email => {
        const i = (report.emails || []).findIndex(e => e === email);
        if (i >= 0) {
            report.emails.splice(i, 1);
            setReport({ ...report });
        }
    };

    const handleRunnow = () => {
        DashboardService.report(props.dashboard.uuid, {
            ...props.dashboard,
            ...{ scope, report, schedule, title }
        });
    };

    const handleRangeChange = range => {
        setRange({ ...range });
        setSchedule({ ...schedule, ...range });
    };

    const isValid = () => {
        // this guys can't be invalid even if they aren't being used
        if (!range.quick && !Moment(range.start).isValid()) return false;
        if (range.end && !Moment(range.end).isValid()) return false;
        if (range.end && Moment(range.start).isAfter(Moment(range.end))) return false;

        return checkValidity(daForm.get());
    };

    useEffect(() => {
        if (!props.dashboard) return;

        setScope(props.dashboard.scope);
        setReport({ ...DEFAULT_REPORT, ...props.dashboard.report });
        setSchedule({ ...DEFAULT_SCHDULE, ...props.dashboard.schedule });
        setLayout({ ...DEFAULT_LAYOUT, ...props.dashboard.layout });
        setTitle(props.dashboard.title);
        setRange({
            start: props.dashboard.schedule.start,
            end: props.dashboard.schedule.end,
            quick: props.dashboard.schedule.quick,
        });
        setOldTitle(props.dashboard.title);
        setNames();
    }, [props.dashboard]);

    useEffect(() => {
        (async () => {
            const n = await DashboardService.next(
                props.dashboard.uuid,
                { schedule }
            );
            n && setNextRun(n.next);
        })();
    }, [props.dashboard.uuid, schedule]);

    // reposition hook to make sure the dialog doesn't go off screen on those little laptops
    useEffect(() => {
        if (setReposition === 0) return;
        setReposition(current => current + 1);
    }, [schedule.date_override]);

    return (
        <Popper
            className={classes.root}
            style={{ height }}
            open={props.show}
            anchorEl={props.anchorEl}
            placement={'bottom-end'}
            role={undefined} disablePortal>
            <PaperComponent
                kick={reposition}
                className={classes.paper}>
                <div
                    onKeyDown={handleListKeyDown}
                    id="draggable-dialog-title">
                    <DialogTitle className={classes.title}>
                        Dashboard Settings
                        <IconButton
                            aria-label="close"
                            className={classes.closeButton}
                            onClick={props.onCancel}
                            size="large">
                            <CloseIcon fontSize="small"/>
                        </IconButton>
                    </DialogTitle>

                    <form className={classes.root} noValidate autoComplete="off">
                        <DialogContent style={{ paddingTop: 0 }}>
                            <div>
                                <Validator
                                    required={true}
                                    unique={names}
                                    autoFocus
                                    style={{ width: 400 }}
                                    label="Title"
                                    aria-label="title"
                                    value={title}
                                    onChange={handleTitleChange}/>
                                <TextField
                                    disabled={props.dashboard.owner !== User().username}
                                    select
                                    label="Sharing"
                                    aria-label="sharing"
                                    value={scope}
                                    onChange={handleScopeChange}>
                                    {SCOPES.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </div>
                            <div>
                                <Validator
                                    style={{ width: 150 }}
                                    valueType="range"
                                    label="Refresh Rate"
                                    aria-label="refresh rate"
                                    value={schedule.interval}
                                    type="number"
                                    onChange={handleIntervalChange}
                                    endAdornment={<InputAdornment position='end'>
                                        Seconds
                                    </InputAdornment>}
                                    inputProps={{ min: 11, max: 3600 }}
                                />
                                <TextField
                                    className={classes.compactTypeWidthFix}
                                    select
                                    label="Compact Type"
                                    aria-label="Compact Type"
                                    value={layout.compact_type}
                                    onChange={handleCompactTypeChange}>
                                    {COMPACTS.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </div>
                        </DialogContent>

                        <DialogContent dividers>
                            <Grid container
                                justifyContent="flex-start"
                                alignItems="flex-start"
                                spacing={2}>
                                <Grid item>
                                    <Typography className={classes.subtitle1}>
                                        Date Range
                                    </Typography>
                                </Grid>
                                <Grid item style={{ marginTop: -5 }}>
                                    <Grid container alignItems="center" spacing={1}>
                                        <Grid item style={{ paddingRight: 0 }}>
                                            <Button
                                                aria-label="widgets"
                                                onClick={handleOverride}>
                                                Widgets
                                            </Button>
                                        </Grid>
                                        <Grid item>
                                            <Switch
                                                checked={!schedule.date_override}
                                                onChange={handleHandleToggleOverride}/>
                                        </Grid>
                                        <Grid item style={{ paddingLeft: 0 }}>
                                            <Button
                                                aria-label="dashboard"
                                                onClick={handleOverrideOff}>
                                                Dashboard
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            {!schedule.date_override &&
                                <DateRange
                                    onChange={handleRangeChange}
                                    schedule={range} />
                            }
                        </DialogContent>

                        <DialogContent dividers>
                            <Typography className={classes.subtitle1}>
                                Report Scheduling
                            </Typography>
                            <TextField
                                style={{ marginRight: 15 }}
                                select
                                label="Period"
                                aria-label="Period"
                                value={schedule.period}
                                onChange={handlePeriodChange}>
                                {PERIODS.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                            {{
                                Hourly:
                                    <HourlyScheduler
                                        schedule={schedule}
                                        onUnitsChange={handleUnitsChange}/>,
                                Daily:
                                    <DailyScheduler
                                        schedule={schedule}
                                        onTimeChange={handleTimeChange}
                                        onUnitsChange={handleUnitsChange} />,
                                Weekday:
                                    <WeekdayScheduler
                                        schedule={schedule}
                                        onWeekdayChange={handleWeekdayChange}
                                        onTimeChange={handleTimeChange}/>,
                                Weekly:
                                    <WeeklyScheduler
                                        schedule={schedule}
                                        onUnitsChange={handleUnitsChange}
                                        onTimeChange={handleTimeChange}/>,
                                Monthly:
                                    <MonthlyScheduler
                                        schedule={schedule}
                                        onTimeChange={handleTimeChange}/>,
                                Yearly:
                                    <YearlyScheduler
                                        schedule={schedule}
                                        onMonthChange={handleMonthChange}
                                        onTimeChange={handleTimeChange}/>,
                                Quarterly:
                                    <QuarterlyScheduler
                                        schedule={schedule}
                                        onTimeChange={handleTimeChange}/>,
                                Quarter:
                                    <QuarterScheduler
                                        schedule={schedule}
                                        onQuarterChange={handleQuarterChange}
                                        onTimeChange={handleTimeChange}/>,
                            }[schedule.period]}
                        </DialogContent>

                        {schedule.period !== 'None' &&
                            <DialogContent style={{ paddingBottom: 0, overflowY: 'clip' }}>
                                <Typography className={classes.subtitle1}>
                                    Email Settings
                                </Typography>
                                <div>
                                    <TextField
                                        style={{ width: '100%' }}
                                        label="Subject"
                                        aria-label="subject"
                                        value={report.subject}
                                        onChange={handleEmailSubjectChange}/>
                                </div>
                                <div>
                                    <InputLabel htmlFor="email-body" className={classes.bodyLabel}>
                                        Body
                                    </InputLabel>
                                    <TextareaAutosize
                                        id="email-body"
                                        style={{ width: '100%', marginLeft: 10 }}
                                        autoComplete="off"
                                        spellCheck={true}
                                        name="body"
                                        className={classes.emailBody}
                                        placeholder="plain text or html"
                                        aria-label="Body"
                                        minRows={2}
                                        value={report.body}
                                        onChange={handleEmailBodyChange}/>
                                </div>
                                <div>
                                    <Validator
                                        label="Email Recipients"
                                        aria-label="Email Recipient"
                                        helperText="Enter additional email recipients"
                                        value={email}
                                        valueType="email"
                                        allowEmpty={true}
                                        unique={report.emails}
                                        onChange={handleEmailChange}/>
                                    <Button
                                        aria-label="add email"
                                        onClick={handleAddEmailRecipient}
                                        color="secondary"
                                        variant="contained"
                                        size="small"
                                        className={classes.addEmailBtn}
                                        disabled={!email || !checkValidity(emailForm.get())}>
                                        Add
                                    </Button>
                                </div>
                                <div style={{ whiteSpace: 'normal' }}>
                                    {(report.emails || []).map(e =>
                                        <Chip
                                            style={{ paddingRight: 5 }}
                                            key={e}
                                            size="small"
                                            color='secondary'
                                            label={e}
                                            aria-label={e}
                                            onDelete={() => handleRemoveEmailRecipient(e)}/>
                                    )}
                                </div>
                                <Grid container
                                    justifyContent="space-between"
                                    alignItems="flex-start"
                                    style={{ margin: 10 }}>
                                    <Grid item>
                                        <Typography variant="body2">
                                            Next Scheduled Run on &nbsp;
                                            <b className={classes.next}>
                                                {nextRun && Moment(nextRun).format(theme.date.long)}
                                            </b>
                                        </Typography>
                                    </Grid>
                                    <Grid item style={{ marginTop: -10 }}>
                                        <Grid container alignItems="center" spacing={1}>
                                            <Grid item style={{ paddingRight: 0 }}>
                                                <Button
                                                    onClick={handlePause}
                                                    aria-label="pause">
                                                    Pause
                                                </Button>
                                            </Grid>
                                            <Grid item>
                                                <Switch
                                                    checked={!schedule.pause}
                                                    onChange={handlePauseChanged}/>
                                            </Grid>
                                            <Grid item style={{ paddingLeft: 0 }}>
                                                <Button
                                                    onClick={handleUnpause}
                                                    aria-label="pause">
                                                    Run
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </DialogContent>
                        }
                        <Grid container
                            alignItems='center'
                            justifyContent='space-between'
                            direction='row'
                            wrap='nowrap'
                            style={{ padding: 8, marginLeft: 10 }}>
                            {schedule.period !== 'None' &&
                                <Grid item>
                                    <Button onClick={handleRunnow} color="secondary">
                                    Run Now
                                    </Button>
                                </Grid>
                            }
                            <Grid container justifyContent='flex-end'>
                                <Button onClick={props.onCancel} color="primary">
                                Cancel
                                </Button>
                                <Button
                                    onClick={handleOnOK}
                                    disabled={!isValid()}
                                    color="primary">
                                OK
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                </div>
            </PaperComponent>
        </Popper>
    );
};
DashboardConfig.propTypes = {
    dashboard: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.shape({
            scope: PropTypes.string,
            owner: PropTypes.string,
            report: PropTypes.object,
            schedule: PropTypes.object,
            layout: PropTypes.object,
            title: PropTypes.string,
            interval: PropTypes.number,
            uuid: PropTypes.string,
            widgets: PropTypes.array,
        })
    ]),
    show: PropTypes.bool.isRequired,
    anchorEl: PropTypes.object,
    onCancel: PropTypes.func.isRequired,
    onOK: PropTypes.func.isRequired,
    names: PropTypes.array.isRequired,
};

const DailyScheduler = props => {
    const classes = useStyles();

    return (
        <React.Fragment>
            <TextField
                style={{ width: 100 }}
                label="Every"
                aria-label="Every"
                value={props.schedule.units}
                type="number"
                onChange={props.onUnitsChange}
                InputProps={{
                    endAdornment: <InputAdornment position="end">
                        Days
                    </InputAdornment>
                }}
            />
            <DatePicker
                style={{ width: 80, marginRight: 0 }}
                margin="normal"
                label="At"
                value={Moment(props.schedule.time, 'HH:mm')}
                inputFormat={'HH:mm'}
                onChange={props.onTimeChange}
                openTo='hours'
                views={['hours', 'minutes']}
                KeyboardButtonProps={{ 'aria-label': 'change time' }}
            />
            <Typography
                component="span"
                className={classes.inputEnd}>
                UTC
            </Typography>
        </React.Fragment>
    );
};
DailyScheduler.propTypes = {
    schedule: PropTypes.object.isRequired,
    onTimeChange: PropTypes.func.isRequired,
    onUnitsChange: PropTypes.func.isRequired,
};

const HourlyScheduler = props => {
    return (
        <TextField
            style={{ width: 150 }}
            label="Every"
            aria-label="Every"
            value={props.schedule.units}
            type="number"
            onChange={props.onUnitsChange}
            InputProps={{
                endAdornment: <InputAdornment position="end">
                    Hours
                </InputAdornment>
            }}
        />
    );
};
HourlyScheduler.propTypes = {
    schedule: PropTypes.object.isRequired,
    onUnitsChange: PropTypes.func.isRequired,
};

const WeekdayScheduler = props => {
    const classes = useStyles();

    return (
        <React.Fragment>
            <TextField
                select
                label="On"
                aria-label="On"
                value={`${props.schedule.weekday}`}
                onChange={props.onWeekdayChange}>
                {WEEKDAYS.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                        {option.value}
                    </MenuItem>
                ))}
            </TextField>
            <DatePicker
                style={{ width: 80, marginRight: 0 }}
                margin="normal"
                label="At"
                inputFormat={'HH:mm'}
                value={Moment(props.schedule.time, 'HH:mm')}
                onChange={props.onTimeChange}
                KeyboardButtonProps={{ 'aria-label': 'change time' }}
            />
            <Typography
                component="span"
                className={classes.inputEnd}>
                UTC
            </Typography>
        </React.Fragment>
    );
};
WeekdayScheduler.propTypes = {
    schedule: PropTypes.object.isRequired,
    onTimeChange: PropTypes.func.isRequired,
    onWeekdayChange: PropTypes.func.isRequired,
};

const WeeklyScheduler = props => {
    const classes = useStyles();

    return (
        <React.Fragment>
            <TextField
                style={{ width: 150 }}
                label="Every"
                aria-label="Every"
                value={props.schedule.units}
                type="number"
                onChange={props.onUnitsChange}
                InputProps={{
                    endAdornment: <InputAdornment position="end">
                        Weeks
                    </InputAdornment>
                }}
            />
            <DatePicker
                style={{ width: 80, marginRight: 0 }}
                margin="normal"
                label="At"
                inputFormat={'HH:mm'}
                value={Moment(props.schedule.time, 'HH:mm')}
                onChange={props.onTimeChange}
                KeyboardButtonProps={{ 'aria-label': 'change time' }}
            />
            <Typography
                component="span"
                className={classes.inputEnd}>
                UTC
            </Typography>
        </React.Fragment>
    );
};
WeeklyScheduler.propTypes = {
    schedule: PropTypes.object.isRequired,
    onTimeChange: PropTypes.func.isRequired,
    onUnitsChange: PropTypes.func.isRequired,
};

const MonthlyScheduler = props => {
    const classes = useStyles();

    return (
        <React.Fragment>
            <Typography
                component="span"
                className={classes.wrapText} style={{ maxWidth: 100 }}>
                On the {FIRST} of the Month
            </Typography>
            <DatePicker
                style={{ width: 80, marginRight: 0 }}
                margin="normal"
                label="At"
                inputFormat={'HH:mm'}
                value={Moment(props.schedule.time, 'HH:mm')}
                onChange={props.onTimeChange}
                KeyboardButtonProps={{ 'aria-label': 'change time' }}
            />
            <Typography
                component="span"
                className={classes.inputEnd}>
                UTC
            </Typography>
        </React.Fragment>
    );
};
MonthlyScheduler.propTypes = {
    schedule: PropTypes.object.isRequired,
    onTimeChange: PropTypes.func.isRequired,
};

const YearlyScheduler = props => {
    const classes = useStyles();

    return (
        <React.Fragment>
            <TextField
                select
                label="On"
                aria-label="On"
                value={`${props.schedule.month}`}
                onChange={props.onMonthChange}>
                {MONTHS.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                        {option.value}
                    </MenuItem>
                ))}
            </TextField>
            <Typography
                component="span"
                className={classes.inputEnd}>
                {FIRST}
            </Typography>
            <DatePicker
                style={{ width: 80, marginRight: 0 }}
                margin="normal"
                label="At"
                inputFormat={'HH:mm'}
                value={Moment(props.schedule.time, 'HH:mm')}
                onChange={props.onTimeChange}
                KeyboardButtonProps={{ 'aria-label': 'change time' }}
            />
            <Typography
                component="span"
                className={classes.inputEnd}>
                UTC
            </Typography>
        </React.Fragment>
    );
};
YearlyScheduler.propTypes = {
    schedule: PropTypes.object.isRequired,
    onTimeChange: PropTypes.func.isRequired,
    onMonthChange: PropTypes.func.isRequired,
};

const QuarterlyScheduler = props => {
    const classes = useStyles();

    return (
        <React.Fragment>
            <Typography
                component="span"
                className={classes.wrapText} style={{ maxWidth: 100 }}>
                On the {FIRST} day of the Quarter
            </Typography>
            <DatePicker
                style={{ width: 80, marginRight: 0 }}
                margin="normal"
                label="At"
                inputFormat={'HH:mm'}
                value={Moment(props.schedule.time, 'HH:mm')}
                onChange={props.onTimeChange}
                KeyboardButtonProps={{ 'aria-label': 'change time' }}
            />
            <Typography
                component="span"
                className={classes.inputEnd}>
                UTC
            </Typography>
        </React.Fragment>
    );
};
QuarterlyScheduler.propTypes = {
    schedule: PropTypes.object.isRequired,
    onTimeChange: PropTypes.func.isRequired,
};

const QuarterScheduler = props => {
    const classes = useStyles();

    return (
        <React.Fragment>
            <TextField
                select
                label="On"
                aria-label="On"
                value={`${props.schedule.quarter}`}
                onChange={props.onQuarterChange}>
                {QUARTERS.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                        {option.value}
                    </MenuItem>
                ))}
            </TextField>
            <Typography
                component="span"
                className={classes.wrapText} style={{ maxWidth: 100 }}>
                On the {FIRST} Day
            </Typography>
            <DatePicker
                style={{ width: 80, marginRight: 0 }}
                margin="normal"
                label="At"
                inputFormat={'HH:mm'}
                value={Moment(props.schedule.time, 'HH:mm')}
                onChange={props.onTimeChange}
                KeyboardButtonProps={{ 'aria-label': 'change time' }}
            />
            <Typography
                component="span"
                className={classes.inputEnd}>
                UTC
            </Typography>
        </React.Fragment>
    );
};
QuarterScheduler.propTypes = {
    schedule: PropTypes.object.isRequired,
    onTimeChange: PropTypes.func.isRequired,
    onQuarterChange: PropTypes.func.isRequired,
};

export {
    DashboardConfig as default,
    SCOPES, PRIVAT, PUBLICR, PUBLICW,
};
